import React, { useEffect, useState } from 'react';
import { Settings, Database, Shield, Bell, RefreshCw } from 'lucide-react';

interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'error';
  message: string | null;
  response_time_ms: number | null;
}

interface SystemStatus {
  keycloak: ServiceStatus;
  database: ServiceStatus;
}

export default function SystemConfig() {
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await fetch('/api/system-status');
      const data = await res.json();
      setSystemStatus(data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch system status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'text-green-600';
      case 'offline':
        return 'text-red-600';
      case 'error':
        return 'text-yellow-600';
      default:
        return 'text-slate-500';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'online':
        return 'bg-green-100';
      case 'offline':
        return 'bg-red-100';
      case 'error':
        return 'bg-yellow-100';
      default:
        return 'bg-slate-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'error':
        return 'Fehler';
      default:
        return 'Unbekannt';
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">System-Konfiguration</h1>
          <p className="text-slate-500 mt-1">Systemweite Einstellungen und Status</p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdate && (
            <span className="text-sm text-slate-500">
              Aktualisiert: {lastUpdate.toLocaleTimeString('de-DE')}
            </span>
          )}
          <button
            onClick={fetchStatus}
            className="p-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm"
            title="Jetzt aktualisieren"
          >
            <RefreshCw size={18} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Keycloak Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Shield size={20} className="text-sayses-accent" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Keycloak</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">URL</span>
              <span className="text-slate-800">https://auth.sempara.de</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Tenant Realm</span>
              <span className="text-slate-800">sayses</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">System Realm</span>
              <span className="text-slate-800">sayses-system</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Status</span>
              {systemStatus ? (
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusBg(systemStatus.keycloak.status)} ${getStatusColor(systemStatus.keycloak.status)}`}>
                  {getStatusText(systemStatus.keycloak.status)}
                  {systemStatus.keycloak.response_time_ms && ` (${systemStatus.keycloak.response_time_ms}ms)`}
                </span>
              ) : (
                <span className="text-slate-400">Laden...</span>
              )}
            </div>
            {systemStatus?.keycloak.status !== 'online' && systemStatus?.keycloak.message && (
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Fehler</span>
                <span className="text-red-600 text-sm">{systemStatus.keycloak.message}</span>
              </div>
            )}
          </div>
        </div>

        {/* Database Status */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Database size={20} className="text-sayses-accent" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Datenbank</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Host</span>
              <span className="text-slate-800">localhost</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Datenbank</span>
              <span className="text-slate-800">sayses</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Status</span>
              {systemStatus ? (
                <span className={`px-2 py-1 rounded-full text-xs ${getStatusBg(systemStatus.database.status)} ${getStatusColor(systemStatus.database.status)}`}>
                  {getStatusText(systemStatus.database.status)}
                </span>
              ) : (
                <span className="text-slate-400">Laden...</span>
              )}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Bell size={20} className="text-sayses-accent" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Benachrichtigungen</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">E-Mail Provider</span>
              <span className="text-yellow-600">Nicht konfiguriert</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">SMS Provider</span>
              <span className="text-yellow-600">Nicht konfiguriert</span>
            </div>
          </div>
        </div>

        {/* Environment Info */}
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Settings size={20} className="text-sayses-accent" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">Umgebung</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Version</span>
              <span className="text-slate-800">1.0.0</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Umgebung</span>
              <span className="text-slate-800">Production</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">API Endpoint</span>
              <span className="text-slate-800">/api</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-500">Auto-Refresh</span>
              <span className="text-slate-800">10 Sekunden</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-slate-800 mb-4">Aktionen</h2>
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap gap-4">
            <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors">
              Cache leeren
            </button>
            <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors">
              Logs anzeigen
            </button>
            <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors">
              Backup erstellen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
